<script>
/**
 * O SINAL de atividade da linha de raciocínio — a "molécula": um núcleo e três
 * satélites ligados por braços, em `--primary`, com três modos.
 *
 * ★ [owner, 21/09/2026] Segunda receita do dia. A primeira era um ponto
 * (pulso simples / órbita de três satélites); o owner trocou pela molécula e
 * mandou o SVG e as três animações — aqui estão, byte a byte nos números; só
 * a cor virou `currentColor` (o verde fixo dele era o primary de lá).
 *
 * | modo | quando | o que se vê |
 * |---|---|---|
 * | `estatico` | turno parado (concluído, histórico, erro) | a molécula quieta |
 * | `pulso` | o genérico "Pensando..." | a molécula inteira gira devagar (6s) e os satélites respiram escalonados (1.6s) |
 * | `orbita` | uma etapa de verdade (tool, fase, nó) | cada braço gira por conta própria (1.5s / 2.1s ao contrário / 2.7s), os satélites faíscam (0.9s) e o núcleo lateja |
 *
 * ⚠️ A caixa é de `tamanho` px (22 por padrão, o número do owner) e o SVG não
 * muda de tamanho entre os modos: trocar de modo nunca move o texto ao lado.
 * `transform-box: view-box` nos braços (giram em volta do centro 12,12 do
 * `viewBox`) e `fill-box` nos satélites/núcleo (escalam em volta de si).
 *
 * `prefers-reduced-motion`: os três modos mostram a molécula quieta.
 * Sem `?.`/`??`.
 */
export const MODOS_DO_INDICADOR = Object.freeze({
  ESTATICO: 'estatico',
  PULSO: 'pulso',
  ORBITA: 'orbita',
})

const MODOS = Object.values(MODOS_DO_INDICADOR)

/** A cor padrão do sinal — a marca do escritório (owner: "tem que ser primary"). */
export const COR_PADRAO_DO_INDICADOR = 'var(--primary)'

/** A caixa do owner: `.mol { width: 22px; height: 22px }`. */
export const TAMANHO_PADRAO_DO_INDICADOR = 22

/** Os três braços: cada um é a linha do núcleo ao satélite, e o satélite. */
const BRACOS = Object.freeze([
  { x: 4.5, y: 17.5 },
  { x: 12, y: 4.5 },
  { x: 19.5, y: 8.5 },
])

export default {
  name: 'IntelligenceV2IndicadorDeAtividade',
  props: {
    modo: {
      type: String,
      default: MODOS_DO_INDICADOR.ESTATICO,
      validator: (valor) => MODOS.indexOf(valor) !== -1,
    },
    // Um token CSS (`var(--danger-text)` no erro), nunca hex.
    cor: {
      type: String,
      default: COR_PADRAO_DO_INDICADOR,
    },
    tamanho: {
      type: Number,
      default: TAMANHO_PADRAO_DO_INDICADOR,
    },
  },
  computed: {
    bracos() {
      return BRACOS
    },
    classes() {
      return {
        'indicador-de-atividade--pulso': this.modo === MODOS_DO_INDICADOR.PULSO,
        'indicador-de-atividade--orbita': this.modo === MODOS_DO_INDICADOR.ORBITA,
      }
    },
    estilo() {
      return { color: this.cor, width: this.tamanho + 'px', height: this.tamanho + 'px' }
    },
  },
}
</script>

<template>
  <!-- Decorativo: quem ANUNCIA o estado é o texto da linha ao lado. -->
  <svg
    class="indicador-de-atividade"
    :class="classes"
    :style="estilo"
    :data-modo="modo"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    data-teste="indicador-de-atividade"
  >
    <g class="indicador-de-atividade__orbita">
      <g
        v-for="(braco, i) in bracos"
        :key="i"
        class="indicador-de-atividade__braco"
        data-teste="indicador-braco"
      >
        <line
          class="indicador-de-atividade__linha"
          x1="12"
          y1="12"
          :x2="braco.x"
          :y2="braco.y"
          stroke="currentColor"
          stroke-width="1.1"
          stroke-linecap="round"
        />
        <circle
          class="indicador-de-atividade__satelite"
          :cx="braco.x"
          :cy="braco.y"
          r="2"
          fill="currentColor"
          data-teste="indicador-satelite"
        />
      </g>
    </g>
    <circle
      class="indicador-de-atividade__nucleo"
      cx="12"
      cy="12"
      r="3.1"
      fill="currentColor"
      data-teste="indicador-nucleo"
    />
  </svg>
</template>

<style lang="sass" scoped>
// Os números são os do owner (21/09/2026): opacidade dos braços, tempos e
// escalas de cada modo. A cor é `currentColor` (a prop `cor`, via `color`).
$opacidade-da-linha: 0.38
$giro-do-pulso: 6s
$respiro-do-satelite: 1.6s
$giro-do-braco-1: 1.5s
$giro-do-braco-2: 2.1s
$giro-do-braco-3: 2.7s
$faisca-do-satelite: 0.9s
$latejo-do-nucleo: 0.9s

.indicador-de-atividade
  display: inline-block
  flex-shrink: 0
  overflow: visible

  &__linha
    opacity: $opacidade-da-linha

  // `view-box`: o braço gira em volta do centro do desenho (12,12), não do
  // próprio retângulo. `fill-box`: satélite e núcleo escalam em volta de si.
  &__orbita,
  &__braco
    transform-box: view-box
    transform-origin: 12px 12px

  &__satelite,
  &__nucleo
    transform-box: fill-box
    transform-origin: center

  // ── pulso: a molécula inteira gira devagar, os satélites respiram ──────
  &--pulso &__orbita
    animation: indicador-giro $giro-do-pulso linear infinite

  &--pulso &__satelite
    animation: indicador-respiro $respiro-do-satelite ease-in-out infinite

  &--pulso &__braco:nth-of-type(2) &__satelite
    animation-delay: 0.25s

  &--pulso &__braco:nth-of-type(3) &__satelite
    animation-delay: 0.5s

  // ── órbita: cada braço por conta própria, satélites faíscam, núcleo lateja ─
  &--orbita &__braco
    animation: indicador-giro $giro-do-braco-1 linear infinite

  &--orbita &__braco:nth-of-type(2)
    animation-name: indicador-giro-inverso
    animation-duration: $giro-do-braco-2

  &--orbita &__braco:nth-of-type(3)
    animation-duration: $giro-do-braco-3

  &--orbita &__satelite
    animation: indicador-faisca $faisca-do-satelite ease-in-out infinite

  &--orbita &__braco:nth-of-type(2) &__satelite
    animation-delay: 0.3s

  &--orbita &__braco:nth-of-type(3) &__satelite
    animation-delay: 0.6s

  &--orbita &__nucleo
    animation: indicador-latejo $latejo-do-nucleo ease-in-out infinite

@keyframes indicador-giro
  from
    transform: rotate(0deg)
  to
    transform: rotate(360deg)

@keyframes indicador-giro-inverso
  from
    transform: rotate(360deg)
  to
    transform: rotate(0deg)

@keyframes indicador-respiro
  0%, 100%
    transform: scale(0.75)
    opacity: 0.5
  50%
    transform: scale(1.15)
    opacity: 1

@keyframes indicador-faisca
  0%, 100%
    transform: scale(0.6)
    opacity: 0.45
  50%
    transform: scale(1.3)
    opacity: 1

@keyframes indicador-latejo
  0%, 100%
    transform: scale(0.9)
  50%
    transform: scale(1.12)

@media (prefers-reduced-motion: reduce)
  .indicador-de-atividade
    &__orbita,
    &__braco,
    &__satelite,
    &__nucleo
      animation: none
</style>
